import { Container } from "../../../components/Container";
import PageTitle from "../../../components/PageTitle";
import { Heading } from "../../../components/UiComponents/heading";
import { Client, College, ReferenceItemDTO, ReferenceType, StudentAwardDTO } from "../../../api/client";
import { useEffect, useState } from "react";
import ManageSection from "./ManageSection";
import { AuthContextState } from "../../../components/AuthWrapper";
import { SkeletonLoader } from "../../../components/NotAuthorized/SkeletonLoader";
import { StudentAwardForm } from "./AddStudentAwardForm";
import AddStudentAwardModal from "./AddStudentAwardForm";
import toast from "react-hot-toast";
import { normalizeNetId } from "../../../helpers/stringHelpers";

interface AwardsProgramsProps {
  client: Client | null,
  state: AuthContextState | null
}

export default function AwardsAndProgramsManager({client, state}: AwardsProgramsProps)  {
  const [awards, setAwards] = useState<ReferenceItemDTO[] | undefined>(); // holds list of awards
  const [programs, setPrograms] = useState<ReferenceItemDTO[] | undefined>(); // holds list of programs
  const [colleges, setColleges] = useState<College[] | undefined>(); // holds list of colleges

  const [newAwardName, setNewAwardName] = useState<string>(""); // holds input for entering an award name
  const [newProgramName, setNewProgramName] = useState<string>(""); // holds input for entering program name

  const [loading, setLoading] = useState<boolean>(true); // is true while we wait for awards,programs,colleges to populate from DB

    const fetchData = async () => {
      
      // Wait for user and admin state to load, otherwise the backend will throw authentication errors
      if (state && (!state.user || !state.user.admin)) return;
      
      setLoading(true);

      const fetchedAwards = await client?.admin_GetReferenceItems(ReferenceType.Award);
      const fetchedPrograms = await client?.admin_GetReferenceItems(ReferenceType.Program);
      const fetchedColleges = await client?.admin_GetColleges();

      setAwards(fetchedAwards);
      setPrograms(fetchedPrograms);
      setColleges(fetchedColleges);
      
      setLoading(false);
  };

  // Determine if we are adding an Award or Program, then call the method depending on which type, then update our list of items
  const onAdd = async (type: ReferenceType, name: string) => {
    try {
    if (type == ReferenceType.Award) {
      await client?.admin_AddReferenceItem(ReferenceType.Award, name)
      // Call the method to update our Awards to get a correctly updated list
      const updated = await client?.admin_GetReferenceItems(ReferenceType.Award);
      setAwards(updated);
      toast.success("Award added successfully");
    }

    else if (type == ReferenceType.Program) {
      await client?.admin_AddReferenceItem(ReferenceType.Program, name);
      const updated = await client?.admin_GetReferenceItems(ReferenceType.Program);
      setPrograms(updated);
      toast.success("Program added successfully");
    }
  } catch (error) {
    toast.error(`Failed to add ${type === ReferenceType.Award ? 'award' : 'program'}. Please try again.`);
  } finally {
    if (type === ReferenceType.Award) setNewAwardName('');
    if (type === ReferenceType.Program) setNewProgramName('');
  }
  }

  // Determine if soft-deleting Award or Program, then call correct method
  const onRemove = async (type: ReferenceType, id: string) => {
    if (type == ReferenceType.Award) {
      await client?.admin_RemoveReferenceItem(ReferenceType.Award, id);
      // We don't need to call an extra method, we can just filter out the one we just deleted and update visuals on the front-end
      setAwards((prev) => prev?.filter((a) => a.id != id));
    }
    else if (type == ReferenceType.Program) {
      await client?.admin_RemoveReferenceItem(ReferenceType.Program, id);
      setPrograms((prev) => prev?.filter((a) => a.id != id));
    }
  }

  const onEdit = async (type: ReferenceType, id: string, newName: string) => {
    try {
      if (type == ReferenceType.Award) {
        await client?.admin_UpdateReferenceItem(ReferenceType.Award, id, newName);
        const updated = await client?.admin_GetReferenceItems(ReferenceType.Award);
        setAwards(updated);
        toast.success("Successfully edited award!")
      }

      if (type == ReferenceType.Program) {
        await client?.admin_UpdateReferenceItem(ReferenceType.Program, id, newName);
        const updated = await client?.admin_GetReferenceItems(ReferenceType.Program);
        setPrograms(updated);
        toast.success("Successfully edited program!")
      }
    } catch (error) {
      toast.error(`Failed to edit ${type === ReferenceType.Award ? 'award' : 'program'}. Please try again.`);
    }
  }

  const handleAddStudentAward = async(form: StudentAwardForm) => {
    const normalizedID: string = normalizeNetId(form.netId!.trim()) // trim white space and remove @usf.edu

    const model = new StudentAwardDTO({
      netID: normalizedID,
      startAcademicYear: form.startAcademicYear!,
      collegeId: form.collegeId!,
      awardId: form.awardId!,
      awardStatus: form.awardStatus,
      programId: form.programId!,
      programDetails: form.programDetails,
      isUndergraduate: form.isUndergraduate,
      estimatedAmount: form.estimatedAmount ?? 0
    })

    await client?.admin_AddStudentAward(model)
      .then(() => {
        console.log("Award saved: ", model)
        toast.success("Award successfully added!")
      })
      .catch((error) => {
        if (error.response && error.status === 404) {
          toast.error("Unable to find a student with that UID", {duration: 3000});
        } else {
          toast.error("An error occurred when attempting to add this award.", {duration: 3000});
        }

        throw error
      });
  }

  useEffect(() => {
    fetchData();
  }, [])

  if (loading) return <SkeletonLoader/>

  return (
        <Container>
          <PageTitle title="Awards & Programs Dashboard"/>
          <Heading text="Awards & Programs Dashboard"/>

          <div className="mt-6 space-y-6">
            <ManageSection
              title="Awards"
              items={awards}
              placeholder="Enter Award Name..."
              onAdd={(name) => onAdd(ReferenceType.Award, name)}
              onRemove={(id) => onRemove(ReferenceType.Award, id)}
              inputValue={newAwardName}
              onInputChange={setNewAwardName}
              onEdit={(id, newName) => onEdit(ReferenceType.Award, id, newName)}
            />

            <ManageSection
              title="Programs"
              items={programs}
              placeholder="Enter Program Name..."
              onAdd={(name) => onAdd(ReferenceType.Program, name)}
              onRemove={(id) => onRemove(ReferenceType.Program, id)}
              inputValue={newProgramName}
              onInputChange={setNewProgramName}
              onEdit={(id, newName) => onEdit(ReferenceType.Program, id, newName)}
            />

            <AddStudentAwardModal 
              isOpen={true}
              onClose={() => {}}
              onSubmit={(form) => handleAddStudentAward(form)}
              awards={awards!}
              programs={programs!}
              colleges={colleges!}
              isModal={false}
            />
          </div>
        </Container>
  );
}
